import { Controller } from "@hotwired/stimulus"

// Multi-select combobox with tag chips and autocomplete filtering
export default class extends Controller {
  static targets = ["input", "list", "option", "tags", "hiddenInputs"]
  static values = { name: String, selected: Array }

  connect() {
    this.selectedValues = new Set(this.selectedValue)
    this.allOptions = this.optionTargets.map(el => ({
      element: el,
      value: el.dataset.value,
      label: el.dataset.label
    }))

    this.isOpen = false
    this.highlightedIndex = -1

    this.boundClickOutside = this.clickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)

    this.renderTags()
    this.renderHiddenInputs()
    this.updateOptionCheckmarks()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
  }

  input() {
    const query = this.inputTarget.value.toLowerCase().trim()
    this.filterOptions(query)
    this.open()
  }

  focus() {
    this.open()
    this.filterOptions(this.inputTarget.value.toLowerCase().trim())
  }

  keydown(event) {
    const visible = this.visibleOptions
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        if (!this.isOpen) { this.open() } else { this.highlightNext() }
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightPrevious()
        break
      case "Enter":
        event.preventDefault()
        if (this.isOpen && this.highlightedIndex >= 0) {
          this.toggleOption(visible[this.highlightedIndex])
        }
        break
      case "Escape":
        this.close()
        break
    }
  }

  select(event) {
    this.toggleOption(event.currentTarget)
  }

  highlight(event) {
    const visible = this.visibleOptions
    const index = visible.indexOf(event.currentTarget)
    if (index >= 0) {
      this.highlightedIndex = index
      this.updateHighlight()
    }
  }

  removeTag(event) {
    const value = event.currentTarget.dataset.value
    this.selectedValues.delete(value)
    this.renderTags()
    this.renderHiddenInputs()
    this.updateOptionCheckmarks()
  }

  toggleOption(optionElement) {
    if (!optionElement) return
    const value = optionElement.dataset.value

    if (this.selectedValues.has(value)) {
      this.selectedValues.delete(value)
    } else {
      this.selectedValues.add(value)
    }

    this.inputTarget.value = ""
    this.filterOptions("")
    this.renderTags()
    this.renderHiddenInputs()
    this.updateOptionCheckmarks()
    this.inputTarget.focus()
  }

  filterOptions(query) {
    this.allOptions.forEach(opt => {
      const matches = query === "" || opt.label.toLowerCase().includes(query)
      opt.element.classList.toggle("hidden", !matches)
    })
    this.highlightedIndex = -1
    this.updateHighlight()
  }

  renderTags() {
    this.tagsTarget.innerHTML = ""
    this.selectedValues.forEach(value => {
      const opt = this.allOptions.find(o => o.value === value)
      if (!opt) return
      const tag = document.createElement("span")
      tag.className = "inline-flex items-center gap-1 rounded-full bg-primary-100 text-primary-800 text-xs font-medium px-2 py-0.5"
      tag.innerHTML = `
        ${opt.label}
        <button type="button" data-action="click->multiselect-combobox#removeTag" data-value="${value}"
                class="ml-0.5 hover:text-primary-600 focus:outline-none" aria-label="Remover">
          <svg class="size-3" viewBox="0 0 20 20" fill="currentColor"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/></svg>
        </button>
      `
      this.tagsTarget.appendChild(tag)
    })
  }

  renderHiddenInputs() {
    this.hiddenInputsTarget.innerHTML = ""
    if (this.selectedValues.size === 0) {
      const empty = document.createElement("input")
      empty.type = "hidden"
      empty.name = this.nameValue
      empty.value = ""
      this.hiddenInputsTarget.appendChild(empty)
    } else {
      this.selectedValues.forEach(value => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = this.nameValue
        input.value = value
        this.hiddenInputsTarget.appendChild(input)
      })
    }
  }

  updateOptionCheckmarks() {
    this.allOptions.forEach(opt => {
      const check = opt.element.querySelector("[data-checkmark]")
      const selected = this.selectedValues.has(opt.value)
      if (check) {
        check.classList.toggle("invisible", !selected)
        check.classList.toggle("text-primary-600", selected)
        check.classList.toggle("text-white", false)
      }
      opt.element.classList.toggle("font-medium", selected)
    })
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    this.listTarget.classList.remove("hidden")
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.listTarget.classList.add("hidden")
    this.highlightedIndex = -1
    this.updateHighlight()
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  get visibleOptions() {
    return this.optionTargets.filter(el => !el.classList.contains("hidden"))
  }

  highlightNext() {
    const visible = this.visibleOptions
    if (!visible.length) return
    this.highlightedIndex = Math.min(this.highlightedIndex + 1, visible.length - 1)
    this.updateHighlight()
    this.scrollToHighlighted()
  }

  highlightPrevious() {
    const visible = this.visibleOptions
    if (!visible.length) return
    this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0)
    this.updateHighlight()
    this.scrollToHighlighted()
  }

  updateHighlight() {
    this.visibleOptions.forEach((el, i) => {
      el.classList.toggle("bg-primary-600", i === this.highlightedIndex)
      el.classList.toggle("text-white", i === this.highlightedIndex)
      el.classList.toggle("text-gray-900", i !== this.highlightedIndex)
    })
  }

  scrollToHighlighted() {
    const visible = this.visibleOptions
    if (this.highlightedIndex >= 0 && visible[this.highlightedIndex]) {
      visible[this.highlightedIndex].scrollIntoView({ block: "nearest" })
    }
  }
}
